import React, { useState, useContext, useEffect, useReducer } from "react";
import { ConfigurationContext } from "contexts/ConfigurationContext";
import Extension from "./Extension";
import App from "./App";
import { useSteps, useLogger, useAnalytics } from "hooks";
import Error from "./Error";
import MessageAppCancelError from "./MessageAppCancelError";
import MessageAppTimeoutError from "./MessageAppTimeoutError";
import MessageExtensionTimeoutError from "./MessageExtensionTimeoutError";
import MessageAppScanError from "./MessageAppScanError";
import Fade from "components/Fade";

//import { collapseTextChangeRangesAcrossMultipleVersions, createPartiallyEmittedExpression } from "typescript";

const stepName = "appDetection";

/**
 *  AppDetection
 *
 *  The app detection step.
 */
const StepAppDetection = (props) => {
  const config = useContext(ConfigurationContext);
  let { steps, setCompleted, setError, previousStep, nextStep } = useSteps();
  const logger = useLogger();
  const analytics = useAnalytics();

  //  State can be:
  //    1. extension - Chrome extension detection.
  //    2. app - Mac or Windows app detection.
  //    3. appCancelError - Application has been canceled.
  //    4. appTimeoutError - Application detection ran out of time.
  //    5. extTimeoutError - Extension detection ran out of time.

  const [state, setState] = useState({});

  /**
   *  Finish and clean up for step. Move onto the next step.
   */
  const finish = () => {
    setCompleted(stepName);
    nextStep();
  };

  /**
   * Determines if this is windows or mac app detection.
   *
   * @returns bool - True if app and false if not.
   */
  const isAppDetection = () => {
    return config.isMacOS || config.isWindows;
  };

  /**
   * Determines if this is Chrome Extension detection.
   *
   * @returns bool - True if extension and false if not.
   */
  const isExtensionDetection = () => {
    return config.isChromeOS;
  };

  /**
   * Done detection handler. Checks for errors and moves to next state or next step.
   * @param {object} evt - The event.
   */
  const onDone = (evt) => {
    if (evt.success) {
      analytics.completed();
      finish();
    } else {
      // Set up the next state as an error.
      if (isAppDetection()) {
        if (evt.error.id == "ScanError") {
          logger.error("App error occurred", "AppError");
          analytics.failed("AppError");

          setState({ appScanError: true });
        }

        if (evt.error.id == "CancelError") {
          logger.error("App execution canceled", "AppCanceled");
          analytics.failed("AppCanceled");

          setState({ appCancelError: true });
        }
        if (evt.error.id == "TimeoutError") {
          logger.error("App execution timed out", "AppTimeout");
          // Derives run vs load from whether the exe ever phoned home. Passing "run"
          // here unconditionally, as this did, made the two indistinguishable and left
          // the load path with no call site at all.
          analytics.appTimedOut();

          setState({ appTimeoutError: true });
        }
      } else if (isExtensionDetection()) {
        if (evt.error.id == "TimeoutError") {
          logger.error("Chrome extension timed out", "ExtensionTimeout");
          analytics.timedOut("extension");
          setState({ extensionTimeoutError: true });
        }
      }
    }
  };

  /**
   *  Determine if the this step can or should run.
   *
   *  @returns bool - True if run and false if not.
   */
  const canRun = () => {
    return config.steps.appDetection && !steps.state.appDetection.completed;
  };

  // Determine the initial state.
  useEffect(
    () => {
      if (canRun()) {
        // Set the state.
        if (config.isChromeOS) {
          setState({ extension: true });
        } else if (config.isMacOS || config.isWindows) {
          setState({ app: true });
        }
      } else {
        // Can't run so go on to the next step.
        finish();
      }
    },
    [] // Once only.
  );

  /**
   *  Retry handler. Restart this step.
   */
  const onRetry = () => {
    // "Download and Run Application" on the Detection Error screen. from_phase records what the
    // visitor was retrying FROM, which is what separates "timed out then tried again" from
    // "timed out and left" - the two halves of the never_started bucket.
    analytics.retried();
    previousStep();
  };

  /**
   *  Cancel detection. Go to error page or go back.
   */
  const onCancel = () => {
    analytics.failed("UserCancelled");
    if (config.error && config.error.length > 0) {
      window.location = config.error;
    } else {
      // No error url so go back.
      window.history.back();
    }
  };

  return (
    <>
      <Fade show="show">
        <div className="mt-5 text-center">
          {state.extension && <Extension onDone={onDone} />}
          {state.app && <App onDone={onDone} />}
          {state.appCancelError && (
            <Error onRetry={onRetry} onContinue={onCancel}>
              <MessageAppCancelError />
            </Error>
          )}
          {state.appTimeoutError && (
            <Error onRetry={onRetry} onContinue={onCancel}>
              <MessageAppTimeoutError />
            </Error>
          )}
          {state.extensionTimeoutError && (
            <Error onRetry={onRetry} onContinue={onCancel}>
              <MessageExtensionTimeoutError />
            </Error>
          )}
          {state.appScanError && (
            <Error onRetry={onRetry} onContinue={onCancel}>
              <MessageAppScanError />
            </Error>
          )}
        </div>
      </Fade>
    </>
  );
};

export default StepAppDetection;
